import { SectionMessage } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { ChatAvatar } from "./ChatAvatar";
import {
  channel,
  fullName,
  phone,
  type ContactDetailResult,
  type ContactPerson,
  type ContactRights,
  type ContactSession,
} from "./contactModel";
import { ContactTagSelector } from "./ContactTagSelector";
import type { ContactWorkspaceController } from "./contactWorkspaceController";

// Ficha del contacto (ContactDetail.razor del portal actual): avatar, nombre, teléfono y etiquetas salen de la fila al
// instante; Datos y Conversaciones, con su esqueleto, hasta que llega la ficha. Sin CSS propio en el portal actual.

const opens = (session: ContactSession | null | undefined): session is ContactSession =>
  !!session && (session.conversationId !== null || session.sessionId !== null);

export function ContactDetail({
  ctl,
  contact,
  value,
  rights,
  rowThread,
  error,
}: {
  ctl: ContactWorkspaceController;
  contact: ContactPerson;
  value: ContactDetailResult | null;
  rights: ContactRights | null | undefined;
  rowThread: ContactSession | null | undefined;
  error: string | null;
}) {
  const first = value === null ? (opens(rowThread) ? rowThread : null) : (value.conversations.find(opens) ?? null);
  const name = fullName(contact);
  return (
    <div className="c1-contact-detail">
      <div className="c1-detail-identity">
        <ChatAvatar name={name} identity={contact.id} external className="c1-detail-avatar" />
        <h3>{name}</h3>
        <p>{phone(contact.phone)}</p>
        {rights?.canOpenChat === true && first ? (
          <button className="button secondary" onClick={() => void ctl.openSession(first)}>
            <ChatIcon name="message-square-text" />
            {ctl.L("OPEN_CHAT_SHORT")}
          </button>
        ) : null}
      </div>
      {error !== null ? <SectionMessage message={error} tone="error" /> : null}
      {value === null ? (
        <section className="c1-detail-skeleton" aria-busy="true">
          <h3>{ctl.L("DATA")}</h3>
          <div aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </section>
      ) : (
        <section>
          <h3>{ctl.L("DATA")}</h3>
          <dl>
            <dt>{ctl.field("Email")}</dt>
            <dd>
              {value.contact.email ?? "—"}{" "}
              {value.contact.emailVerified ? (
                <span className="c1-verified" role="img" title={ctl.L("VERIFIED")} aria-label={ctl.L("VERIFIED")}>
                  <ChatIcon name="check" />
                </span>
              ) : null}
            </dd>
            <dt>{ctl.field("Phone")}</dt>
            <dd>
              {phone(value.contact.phone)}{" "}
              {value.contact.phoneVerified ? (
                <span className="c1-verified" role="img" title={ctl.L("VERIFIED")} aria-label={ctl.L("VERIFIED")}>
                  <ChatIcon name="check" />
                </span>
              ) : null}
            </dd>
            <dt>{ctl.field("RelationshipStatus")}</dt>
            <dd>
              {value.contact.relationshipStatus === null ? ctl.L("UNDEFINED") : ctl.L(value.contact.relationshipStatus)}
            </dd>
          </dl>
        </section>
      )}
      <section>
        <h3>{ctl.field("Tags")}</h3>
        <ContactTagSelector
          key={contact.id}
          api={ctl.api}
          labels={ctl.labels}
          tenant={ctl.tenant}
          user={ctl.user}
          addLabel={ctl.L("ADD")}
          contactId={contact.id}
          value={contact.tags ?? []}
          enableAssign={rights?.canWrite === true}
          colorOf={ctl.tagColor}
          onChanged={() => void ctl.checkContext()}
        />
      </section>
      {value === null ? (
        <section className="c1-sessions c1-detail-skeleton" aria-busy="true">
          <h3>{ctl.L("SESSIONS")}</h3>
          <div aria-hidden="true">
            <span className="c1-skeleton-row"></span>
            <span className="c1-skeleton-row"></span>
          </div>
        </section>
      ) : (
        <>
          <section className="c1-sessions">
            <h3>
              {ctl.L("SESSIONS")} <span className="c1-meta">{value.conversations.length}</span>
            </h3>
            {value.conversations.length === 0 ? <p>{ctl.L("NO_SESSION")}</p> : null}
            {value.conversations.map((session, index) => (
              <article className="c1-session" key={session.sessionId ?? "s" + index}>
                <button
                  type="button"
                  className="c1-conversation-row"
                  disabled={
                    !value.access.canOpenChat || (session.conversationId === null && session.sessionId === null)
                  }
                  onClick={() => void ctl.openSession(session)}
                >
                  <ChatIcon name="message-square-text" />
                  <span>
                    <strong>{session.inbox ?? ctl.field("Inbox")}</strong>
                    <span>{session.agent ?? ctl.L(session.agentEmployeeId !== null ? "AI_AGENT" : "UNASSIGNED")}</span>
                    <time>{ctl.date(session.lastVisibleConversationActivityAt)}</time>
                  </span>
                </button>
                <details className="c1-disclosure">
                  <summary>
                    <ChatIcon name="chevron-down" />
                    {ctl.L("CHANNEL_DATA")}
                  </summary>
                  <dl>
                    <dt>{ctl.field("Channel")}</dt>
                    <dd>{channel(session)}</dd>
                  </dl>
                </details>
              </article>
            ))}
            {value.nextBefore !== null ? (
              <button className="button secondary" onClick={() => void ctl.moreSessions()}>
                {ctl.L("NEXT")}
              </button>
            ) : null}
          </section>
          <details className="c1-disclosure">
            <summary>
              <ChatIcon name="chevron-down" />
              {ctl.L("MORE_DATA")}
            </summary>
            <dl>
              <dt>{ctl.field("CreatedAt")}</dt>
              <dd>{ctl.date(value.contact.createdAt)}</dd>
              <dt>{ctl.field("UpdatedAt")}</dt>
              <dd>{value.contact.updatedAt ? ctl.date(value.contact.updatedAt) : ctl.L("UNCHANGED")}</dd>
            </dl>
          </details>
        </>
      )}
    </div>
  );
}
